import { Injectable } from '@angular/core';
import { Observable, from, BehaviorSubject, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

export interface IDatabaseService {
  // Tablas
  getAllTables(): Observable<any[]>;
  getTableById(id: string): Observable<any | null>;
  createTable(table: any): Observable<any>;
  updateTable(table: any): Observable<any>;
  deleteTable(id: string): Observable<void>;
  
  // Zonas
  getAllZones(): Observable<any[]>;
  getZoneById(id: string): Observable<any | null>;
  createZone(zone: any): Observable<any>;
  updateZone(zone: any): Observable<any>;
  deleteZone(id: string): Observable<void>;
  
  // Órdenes
  getAllOrders(): Observable<any[]>;
  getOrderById(id: string): Observable<any | null>;
  createOrder(order: any): Observable<any>;
  updateOrder(order: any): Observable<any>;
  deleteOrder(id: string): Observable<void>;
  
  // Menú
  getAllMenuItems(): Observable<any[]>;
  getMenuItemById(id: string): Observable<any | null>;
  createMenuItem(item: any): Observable<any>;
  updateMenuItem(item: any): Observable<any>;
  deleteMenuItem(id: string): Observable<void>;
  
  // Facturas
  getAllBills(): Observable<any[]>;
  getBillById(id: string): Observable<any | null>;
  createBill(bill: any): Observable<any>;
  updateBill(bill: any): Observable<any>;
  deleteBill(id: string): Observable<void>;
  
  // Usuarios/Auth
  getUser(): Observable<any | null>;
  setUser(user: any): Observable<void>;
  clearUser(): Observable<void>;
  
  // Utilidades
  clearAll(): Observable<void>;
  isInitialized(): Observable<boolean>;
  setInitialized(): Observable<void>;
  /** Lee un valor del store metadata (para config en modo demo). */
  getMetadata(key: string): Observable<{ key: string; value: any } | null>;
  /** Escribe un valor en el store metadata. */
  setMetadata(key: string, value: any): Observable<void>;
}

@Injectable({
  providedIn: 'root'
})
export class DatabaseService implements IDatabaseService {
  private dbName = 'restora360_db';
  private dbVersion = 4;
  private db: IDBDatabase | null = null;
  private dbReady$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.initDatabase();
  }

  private async initDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Error abriendo IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.dbReady$.next(true);
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Object Stores
        if (!db.objectStoreNames.contains('tables')) {
          const tableStore = db.createObjectStore('tables', { keyPath: 'id' });
          tableStore.createIndex('zoneId', 'zoneId', { unique: false });
        }

        if (!db.objectStoreNames.contains('zones')) {
          db.createObjectStore('zones', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('locations')) {
          db.createObjectStore('locations', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('orders')) {
          const orderStore = db.createObjectStore('orders', { keyPath: 'id' });
          orderStore.createIndex('tableId', 'tableId', { unique: false });
          orderStore.createIndex('status', 'status', { unique: false });
        }

        if (!db.objectStoreNames.contains('menuItems')) {
          db.createObjectStore('menuItems', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('bills')) {
          const billStore = db.createObjectStore('bills', { keyPath: 'id' });
          billStore.createIndex('orderId', 'orderId', { unique: false });
        }

        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('insumos')) {
          db.createObjectStore('insumos', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('recipe')) {
          db.createObjectStore('recipe', { keyPath: 'menuItemId' });
        }
      };
    });
  }

  private waitForDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const subscription = this.dbReady$.subscribe(ready => {
        if (ready && this.db) {
          subscription.unsubscribe();
          resolve(this.db);
        }
      });

      setTimeout(() => {
        if (!this.db) {
          subscription.unsubscribe();
          reject(new Error('Database initialization timeout'));
        }
      }, 5000);
    });
  }

  // Generic CRUD operations
  private getAll<T>(storeName: string): Observable<T[]> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<T[]>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readonly');
          const store = transaction.objectStore(storeName);
          const request = store.getAll();

          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error(`Error getting all ${storeName}:`, error);
        return of([] as T[]);
      })
    );
  }

  private getById<T>(storeName: string, id: string): Observable<T | null> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<T | null>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readonly');
          const store = transaction.objectStore(storeName);
          const request = store.get(id);

          request.onsuccess = () => resolve(request.result || null);
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error(`Error getting ${storeName} by id:`, error);
        return of(null);
      })
    );
  }

  private create<T>(storeName: string, item: T): Observable<T> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<T>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.add(item);

          request.onsuccess = () => resolve(item);
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error(`Error creating ${storeName}:`, error);
        throw error;
      })
    );
  }

  private update<T>(storeName: string, item: T): Observable<T> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<T>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.put(item);

          request.onsuccess = () => resolve(item);
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error(`Error updating ${storeName}:`, error);
        throw error;
      })
    );
  }

  private delete(storeName: string, id: string): Observable<void> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<void>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.delete(id);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error(`Error deleting ${storeName}:`, error);
        throw error;
      })
    );
  }

  // Tables
  getAllTables(): Observable<any[]> {
    return this.getAll<any>('tables');
  }

  getTableById(id: string): Observable<any | null> {
    return this.getById<any>('tables', id);
  }

  createTable(table: any): Observable<any> {
    return this.create('tables', table);
  }

  updateTable(table: any): Observable<any> {
    return this.update('tables', table);
  }

  deleteTable(id: string): Observable<void> {
    return this.delete('tables', id);
  }

  // Zones
  getAllZones(): Observable<any[]> {
    return this.getAll<any>('zones');
  }

  getZoneById(id: string): Observable<any | null> {
    return this.getById<any>('zones', id);
  }

  createZone(zone: any): Observable<any> {
    return this.create('zones', zone);
  }

  updateZone(zone: any): Observable<any> {
    return this.update('zones', zone);
  }

  deleteZone(id: string): Observable<void> {
    return this.delete('zones', id);
  }

  // Locations (Sedes)
  getAllLocations(): Observable<any[]> {
    return this.getAll<any>('locations');
  }

  getLocationById(id: string): Observable<any | null> {
    return this.getById<any>('locations', id);
  }

  createLocation(location: any): Observable<any> {
    return this.create('locations', location);
  }

  updateLocation(location: any): Observable<any> {
    return this.update('locations', location);
  }

  deleteLocation(id: string): Observable<void> {
    return this.delete('locations', id);
  }

  // Orders
  getAllOrders(): Observable<any[]> {
    return this.getAll<any>('orders');
  }

  getOrderById(id: string): Observable<any | null> {
    return this.getById<any>('orders', id);
  }

  createOrder(order: any): Observable<any> {
    return this.create('orders', order);
  }

  updateOrder(order: any): Observable<any> {
    return this.update('orders', order);
  }

  deleteOrder(id: string): Observable<void> {
    return this.delete('orders', id);
  }

  // Menu Items
  getAllMenuItems(): Observable<any[]> {
    return this.getAll<any>('menuItems');
  }

  getMenuItemById(id: string): Observable<any | null> {
    return this.getById<any>('menuItems', id);
  }

  createMenuItem(item: any): Observable<any> {
    return this.create('menuItems', item);
  }

  updateMenuItem(item: any): Observable<any> {
    return this.update('menuItems', item);
  }

  deleteMenuItem(id: string): Observable<void> {
    return this.delete('menuItems', id);
  }

  // Bills
  getAllBills(): Observable<any[]> {
    return this.getAll<any>('bills');
  }

  getBillById(id: string): Observable<any | null> {
    return this.getById<any>('bills', id);
  }

  createBill(bill: any): Observable<any> {
    return this.create('bills', bill);
  }

  updateBill(bill: any): Observable<any> {
    return this.update('bills', bill);
  }

  deleteBill(id: string): Observable<void> {
    return this.delete('bills', id);
  }

  // Users/Auth
  getUser(): Observable<any | null> {
    return this.getAll<any>('users').pipe(
      map(users => users.length > 0 ? users[0] : null)
    );
  }

  setUser(user: any): Observable<void> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<void>((resolve, reject) => {
          const transaction = db.transaction(['users'], 'readwrite');
          const store = transaction.objectStore('users');
          store.clear();
          const request = store.add(user);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error('Error setting user:', error);
        throw error;
      })
    );
  }

  clearUser(): Observable<void> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<void>((resolve, reject) => {
          const transaction = db.transaction(['users'], 'readwrite');
          const store = transaction.objectStore('users');
          const request = store.clear();

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error('Error clearing user:', error);
        throw error;
      })
    );
  }

  // Metadata
  isInitialized(): Observable<boolean> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<boolean>((resolve, reject) => {
          const transaction = db.transaction(['metadata'], 'readonly');
          const store = transaction.objectStore('metadata');
          const request = store.get('initialized');

          request.onsuccess = () => resolve(request.result?.value === true);
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(() => of(false))
    );
  }

  setInitialized(): Observable<void> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<void>((resolve, reject) => {
          const transaction = db.transaction(['metadata'], 'readwrite');
          const store = transaction.objectStore('metadata');
          const request = store.put({ key: 'initialized', value: true });

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error('Error setting initialized:', error);
        throw error;
      })
    );
  }

  getMetadata(key: string): Observable<{ key: string; value: any } | null> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<{ key: string; value: any } | null>((resolve, reject) => {
          const transaction = db.transaction(['metadata'], 'readonly');
          const store = transaction.objectStore('metadata');
          const request = store.get(key);
          request.onsuccess = () => resolve(request.result ?? null);
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(() => of(null))
    );
  }

  setMetadata(key: string, value: any): Observable<void> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<void>((resolve, reject) => {
          const transaction = db.transaction(['metadata'], 'readwrite');
          const store = transaction.objectStore('metadata');
          const request = store.put({ key, value });
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error('Error setting metadata:', error);
        throw error;
      })
    );
  }

  // Insumos
  getAllInsumos(): Observable<any[]> {
    return this.getAll<any>('insumos');
  }

  getInsumoById(id: string): Observable<any | null> {
    return this.getById<any>('insumos', id);
  }

  createInsumo(item: any): Observable<any> {
    return this.create('insumos', item);
  }

  updateInsumo(item: any): Observable<any> {
    return this.update('insumos', item);
  }

  deleteInsumo(id: string): Observable<void> {
    return this.delete('insumos', id);
  }

  // Recetas (menuItemId -> líneas de insumos por unidad de plato)
  getAllRecipes(): Observable<{ menuItemId: string; items: { insumoId: string; quantityPerUnit: number }[] }[]> {
    return this.getAll<any>('recipe');
  }

  getRecipe(menuItemId: string): Observable<{ menuItemId: string; items: { insumoId: string; quantityPerUnit: number }[] } | null> {
    return this.getById<any>('recipe', menuItemId);
  }

  setRecipe(menuItemId: string, items: { insumoId: string; quantityPerUnit: number }[]): Observable<any> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<any>((resolve, reject) => {
          const transaction = db.transaction(['recipe'], 'readwrite');
          const store = transaction.objectStore('recipe');
          const request = store.put({ menuItemId, items });

          request.onsuccess = () => resolve({ menuItemId, items });
          request.onerror = () => reject(request.error);
        }));
      }),
      catchError(error => {
        console.error('Error setting recipe:', error);
        throw error;
      })
    );
  }

  clearAll(): Observable<void> {
    return from(this.waitForDB()).pipe(
      switchMap(db => {
        return from(new Promise<void>((resolve, reject) => {
          const transaction = db.transaction(
            ['tables', 'zones', 'locations', 'orders', 'menuItems', 'bills', 'users', 'metadata', 'insumos', 'recipe'],
            'readwrite'
          );

          let completed = 0;
          const total = 10;

          const checkComplete = () => {
            completed++;
            if (completed === total) {
              resolve();
            }
          };

          transaction.objectStore('tables').clear().onsuccess = checkComplete;
          transaction.objectStore('zones').clear().onsuccess = checkComplete;
          transaction.objectStore('locations').clear().onsuccess = checkComplete;
          transaction.objectStore('orders').clear().onsuccess = checkComplete;
          transaction.objectStore('menuItems').clear().onsuccess = checkComplete;
          transaction.objectStore('bills').clear().onsuccess = checkComplete;
          transaction.objectStore('users').clear().onsuccess = checkComplete;
          transaction.objectStore('metadata').clear().onsuccess = checkComplete;
          transaction.objectStore('insumos').clear().onsuccess = checkComplete;
          transaction.objectStore('recipe').clear().onsuccess = checkComplete;

          transaction.onerror = () => reject(transaction.error);
        }));
      }),
      catchError(error => {
        console.error('Error clearing all:', error);
        throw error;
      })
    );
  }
}
